import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, GraduationCap, BookOpen, Trees, Sprout, Play } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { INTERVIEWS, VIDEOS, WANGARI_QUOTE } from "@/data/content";

const FACTS = [
  { icon: GraduationCap, title: "Educator for nearly two decades", desc: "Teaches at Chicago Public Schools and adjunct writing instructor at Wright College, City Colleges of Chicago. Has taught in Japan and Argentina and worked with incarcerated youth." },
  { icon: BookOpen, title: "Award-winning author", desc: "Author of Seeds of Change (Lee & Low), a bestseller for over a decade, The Story of Environmentalist Wangari Maathai, and The Green Literacy Handbook (Solution Tree Press)." },
  { icon: Trees, title: "Certified TreeKeeper & Forest Guide", desc: "Volunteers caring for Chicago's urban trees, graduated from the Forest Therapy School as a certified Forest Guide in 2025, and serves on the Board of the Green Belt Movement." },
  { icon: Sprout, title: "Founder of Books on Benches", desc: "A small nonprofit placing children's books in family court waiting areas, community legal centers, and lawyers' offices — and co-founder of Green Literacy." },
];

export default function About() {
  useEffect(() => {
    document.title = "Meet Jen — Jen Cullerton Johnson";
  }, []);
  return (
    <>
      <PageHero
        eyebrow="Meet Jen"
        title={["An author, educator, and", <span key="a">speaker at the <em className="text-terra">intersection</em></span>, "of literacy & nature."]}
        lede="Jen Cullerton Johnson writes and teaches so that young people can see themselves as active participants in protecting the planet."
        meta="Chicago, Illinois · MEd Loyola University · MFA University of New Orleans"
      />

      <section className="py-20 lg:py-32" data-testid="about-bio">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="rounded-t-[999px] rounded-b-2xl overflow-hidden border border-line shadow-xl shadow-ink/10 max-w-xs mx-auto lg:mx-0">
                <img src="/images/headshot.jpg" alt="Portrait of Jen Cullerton Johnson" className="w-full h-[380px] object-cover photo-tone" data-testid="about-portrait" />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-txtmut mt-4 text-center lg:text-left">
                Jen Cullerton Johnson — author photo
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-lg overflow-hidden border border-line hidden lg:block">
                <img src="/images/jen-event.jpg" alt="Jen Cullerton Johnson with students at a community event" className="w-full h-64 object-cover photo-tone" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif text-2xl sm:text-3xl text-ink leading-snug mb-10 text-balance">
                "My work is driven by the belief that literacy and environmental education are powerful tools for
                social change."
              </p>
            </Reveal>
            <div className="space-y-10">
              {FACTS.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.06}>
                  <div className="flex gap-5" data-testid={`about-fact-${i + 1}`}>
                    <span className="w-11 h-11 rounded-full bg-goldsoft text-terra flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl text-ink mb-2">{f.title}</h3>
                      <p className="text-txtmut leading-relaxed text-sm sm:text-base">{f.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-12 border-t border-line pt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-txtmut mb-4">Education & honors</p>
                <ul className="space-y-2 text-txtmut text-sm sm:text-base">
                  <li>BA, English Literature & Spanish — Indiana University</li>
                  <li>MEd — Loyola University Chicago</li>
                  <li>MFA, Creative Writing — University of New Orleans</li>
                  <li>Teacher Fellowship, Morton Arboretum (2024–2025) — trees & biodiversity</li>
                  <li>Grants from the Illinois Arts Council · Ambassador's Award, San Miguel de Allende</li>
                  <li>Presenter at ASLE, ChLA, Green Schools Network & NAAEE conferences</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-line py-20 lg:py-28" data-testid="about-video">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative rounded-lg overflow-hidden border border-line group" data-testid="about-video-card">
              <a href={VIDEOS.ksnt} target="_blank" rel="noopener noreferrer" aria-label="Watch the KSNT News feature on YouTube" data-testid="about-video-link">
                <img src="/images/event-discovery-center.jpg" alt="A reading of Seeds of Change at an event" className="w-full h-72 sm:h-96 object-cover photo-tone group-hover:scale-[1.03] transition-transform duration-700" />
                <div className="absolute inset-0 bg-ink/30 flex items-center justify-center">
                  <span className="w-16 h-16 rounded-full bg-paper text-ink flex items-center justify-center group-hover:bg-terra group-hover:text-paper transition-colors duration-300">
                    <Play className="w-6 h-6 fill-current" />
                  </span>
                </div>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow className="mb-6">On the road</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink leading-[1.1] mb-6">
              Bringing the story of Wangari Maathai to young readers everywhere
            </h2>
            <p className="text-txtmut leading-relaxed mb-8">
              From the Kansas Children's Discovery Center to classrooms across the country, Jen's visits are
              energetic, interactive, and tailored to your students — dynamic read-alouds in English or Spanish,
              author Q&A, and eco-art activities.
            </p>
            <Link to="/collaborate" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-terra link-underline" data-testid="about-collaborate-link">
              Plan a visit <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="about-do-good">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Do good</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-8 max-w-3xl text-balance">
              A message from Jen: hope, community, and action intertwine
            </h2>
            <p className="text-txtmut leading-relaxed max-w-3xl mb-14">
              "In every author visit, every book sale, and every event I attend, I share the messages of these
              organizations I deeply believe in — organizations I've gotten to know through years of
              collaboration, some whose boards I've served on. My hope is that you'll be moved to join us in
              this work, bridging the gap between intention and action."
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              { name: "The Green Belt Movement", desc: "Founded by Wangari Maathai — restoring Kenya's forests and empowering communities. Jen serves on its Board of Directors.", url: "https://www.greenbeltmovement.org/" },
              { name: "Creative Chicago Reuse Exchange", desc: "A nonprofit fostering a circular economy by providing repurposed materials to educators, artists, and nonprofits.", url: "https://www.creativechirx.org/" },
            ].map((o, i) => (
              <Reveal key={o.name} delay={i * 0.1}>
                <a href={o.url} target="_blank" rel="noopener noreferrer" className="group block border border-line rounded-lg bg-paper hover:bg-cream p-8 lg:p-10 transition-colors duration-300" data-testid={`about-initiative-${i + 1}`}>
                  <div className="flex items-start justify-between mb-6">
                    <Leaf className="w-6 h-6 text-terra" strokeWidth={1.5} />
                    <ArrowUpRight className="w-5 h-5 text-txtmut/40 group-hover:text-terra group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </div>
                  <h3 className="font-serif text-2xl text-ink mb-3">{o.name}</h3>
                  <p className="text-txtmut text-sm leading-relaxed">{o.desc}</p>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-paper py-20 lg:py-28 relative grain" data-testid="about-quote">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center relative z-10">
          <Reveal>
            <blockquote className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-balance">
              "{WANGARI_QUOTE.text}"
            </blockquote>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.22em] text-gold">— {WANGARI_QUOTE.author}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="about-interviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Interviews & conversations</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-12">Read more about Jen</h2>
          </Reveal>
          <ul className="divide-y divide-line border-y border-line">
            {INTERVIEWS.map((it) => (
              <li key={it.url}>
                <a href={it.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between gap-4 py-6" data-testid={`about-interview-${it.pub.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span>
                    <span className="block font-serif text-xl text-ink group-hover:text-terra transition-colors duration-300">{it.name}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-txtmut mt-1">{it.pub}</span>
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-txtmut/40 group-hover:text-terra transition-colors duration-300 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
